<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type {
    View,
    Scale,
    Range,
    DisplayFilter,
    FavoriteView,
    ViewConfig,
    CalendarLayout,
  } from '../lib/types';
  import { getFavoritePageType, CALENDAR_LAYOUT_LABELS } from '../lib/types';
  import { favoriteViews } from '../lib/stores/favorites';
  import { navigateToFavorite } from '../lib/favoritesNavigation';

  interface Props {
    currentView?: View;
    currentScale?: Scale;
    currentRange?: Range;
    currentDisplay?: DisplayFilter | null;
    viewConfig?: ViewConfig;
    // Calendar page context
    currentPageType?: 'timeline' | 'calendar';
    currentLayout?: CalendarLayout;
  }

  let {
    currentView,
    currentScale,
    currentRange,
    currentDisplay,
    viewConfig,
    currentPageType = 'timeline',
    currentLayout,
  }: Props = $props();

  let favorites = $derived($favoriteViews);

  let hasFavorites = $derived(favorites.length > 0);

  const scaleLabels: Record<Scale, string> = { month: 'Month', week: 'Week', year: 'Year' };
  const viewLabels: Record<View, string> = {
    focus: 'Focus',
    rolling: 'Rolling',
    days: 'Days',
    table: 'Table',
    timeline: 'Timeline',
    gantt: 'Gantt',
    'grid-6x2': 'Grid 6×2',
    'grid-quarters': 'Quarters',
    tasks: 'Tasks',
  };

  // Normalize null/undefined for comparison (backend strips nil values via compact_blank)
  function eq(a: any, b: any): boolean {
    return (a ?? null) === (b ?? null);
  }

  function isActive(fav: FavoriteView): boolean {
    const favPageType = getFavoritePageType(fav);

    if (favPageType === 'calendar') {
      return currentPageType === 'calendar' && eq(fav.layout, currentLayout);
    }

    return (
      currentPageType === 'timeline' &&
      fav.view === currentView &&
      fav.scale === currentScale &&
      eq(fav.range, currentRange) &&
      eq(fav.display, currentDisplay)
    );
  }

  function handleNavigate(fav: FavoriteView) {
    navigateToFavorite(fav, viewConfig);
  }

  const rangeLabels: Record<string, string> = {
    '1-month': '1 Month',
    quarter: 'Quarter',
    '3-months': '3 Months',
    '6-months': '6 Months',
    year: 'Year',
    '1-week': '1 Week',
    month: 'Month',
    '5': '5 Weeks',
    '7': '7 Weeks',
    full: 'Full Year',
    half: 'Half Year',
  };

  function configSummaryText(fav: FavoriteView): string {
    if (getFavoritePageType(fav) === 'calendar') {
      const parts = ['Calendar'];
      if (fav.layout) parts.push(CALENDAR_LAYOUT_LABELS[fav.layout] || fav.layout);
      return parts.join(' · ');
    }
    const parts = [viewLabels[fav.view!], scaleLabels[fav.scale!]];
    if (fav.range) parts.push(rangeLabels[fav.range as string] || String(fav.range));
    if (fav.display && fav.display !== 'both') parts.push(fav.display);
    return parts.join(' · ');
  }

  function getFavIcon(fav: FavoriteView): string {
    return fav.icon || '⭐';
  }
</script>

{#if hasFavorites}
  <div class="favorites-dropdown-container hover-dropdown">
    <button class="favorites-trigger" title="Favorite views">
      <span class="favorites-star">★</span>
      <span class="favorites-label">Favorites</span>
      <svg class="favorites-chevron" viewBox="0 0 10 6" fill="currentColor"
        ><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" /></svg
      >
    </button>

    <div class="favorites-dropdown">
      {#each [...favorites].sort((a, b) => a.position - b.position) as fav, i (fav.id)}
        <button
          class="favorites-item"
          class:active={isActive(fav)}
          onclick={() => handleNavigate(fav)}
          title={configSummaryText(fav)}
        >
          <span class="fav-num">{i + 1}</span>
          <span class="fav-icon">{getFavIcon(fav)}</span>
          <div class="fav-content">
            <span class="fav-name">{fav.name}</span>
            <div class="fav-badges">
              {#if getFavoritePageType(fav) === 'calendar'}
                <span class="fav-badge">Page: Calendar</span>
                {#if fav.layout}
                  <span class="fav-badge"
                    >Layout: {CALENDAR_LAYOUT_LABELS[fav.layout] || fav.layout}</span
                  >
                {/if}
              {:else}
                <span class="fav-badge">View: {viewLabels[fav.view!]}</span>
                <span class="fav-badge">Scale: {scaleLabels[fav.scale!]}</span>
                {#if fav.range}
                  <span class="fav-badge"
                    >Range: {rangeLabels[fav.range as string] || fav.range}</span
                  >
                {/if}
                {#if fav.display && fav.display !== 'both'}
                  <span class="fav-badge">Show: {fav.display}</span>
                {/if}
              {/if}
            </div>
          </div>
          {#if isActive(fav)}
            <span class="fav-check">✓</span>
          {/if}
        </button>
      {/each}
      <button class="favorites-manage-btn" onclick={() => router.get('/settings')}>
        <svg
          class="manage-icon"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path
            d="M6.5 2.5h3l.5 1.5h2l.5 1h-13l.5-1h2l.5-1.5z"
            fill="currentColor"
            stroke="none"
            opacity="0"
          />
          <circle cx="8" cy="8.5" r="2" /><path
            d="M13 8.5a5 5 0 01-.4 2l1 1.2-.8.8-1.2-1a5 5 0 01-2 .4v1.6h-1.2v-1.6a5 5 0 01-2-.4l-1.2 1-.8-.8 1-1.2a5 5 0 01-.4-2H3.4V7.3H5a5 5 0 01.4-2l-1-1.2.8-.8 1.2 1a5 5 0 012-.4V2.3h1.2v1.6a5 5 0 012 .4l1.2-1 .8.8-1 1.2a5 5 0 01.4 2h1.6v1.2H13z"
          />
        </svg>
        Edit favorites
      </button>
    </div>
  </div>
{/if}

<style>
  .favorites-dropdown-container {
    position: relative;
  }

  .favorites-trigger {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    height: 1.5rem;
    line-height: 1;
  }

  .favorites-trigger:hover {
    background: #fef3c7;
    border-color: #f59e0b;
  }

  .favorites-star {
    font-size: 0.6875rem;
    color: #f59e0b;
  }

  .favorites-label {
    font-size: 0.6875rem;
  }

  .favorites-chevron {
    width: 0.5rem;
    height: 0.375rem;
    flex-shrink: 0;
    opacity: 0.5;
  }

  .favorites-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25rem;
    min-width: 14rem;
    max-width: 18rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 50;
    overflow: visible;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    transition-delay: 0.15s;
  }

  /* Invisible bridge between trigger and dropdown to prevent hover gap */
  .favorites-dropdown::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .favorites-dropdown-container:hover > .favorites-dropdown {
    opacity: 1;
    visibility: visible;
    transition-delay: 0s;
  }

  .favorites-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.4375rem 0.625rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
  }

  .favorites-item:hover {
    background: #f9fafb;
  }

  .favorites-item.active {
    background: #eff6ff;
  }

  .fav-num {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f3f4f6;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.1875rem;
    flex-shrink: 0;
  }

  .fav-icon {
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .fav-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  .fav-name {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fav-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1875rem;
    margin-top: 0.125rem;
  }

  .fav-badge {
    font-size: 0.5rem;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.1875rem;
    padding: 0 0.25rem;
    line-height: 1.5;
    white-space: nowrap;
  }

  .favorites-manage-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.4375rem 0.625rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-top: 1px solid #f3f4f6;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
  }

  .favorites-manage-btn:hover {
    color: #374151;
    background: #f9fafb;
  }

  .manage-icon {
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
  }

  .fav-check {
    font-size: 0.625rem;
    color: #2563eb;
    font-weight: 600;
    flex-shrink: 0;
  }
</style>
